import { getRuntimeConfig } from "../../../../../config/runtimeConfig";

const configuredPreviewUrl = getRuntimeConfig("VITE_PARTICIPANT_PREVIEW_URL");

function ensureTrailingSlash(value: string): string {
  return value.endsWith('/') ? value : `${value}/`;
}

function normalizePreviewBaseUrl(value: string): string {
  const trimmedValue = value.trim();

  try {
    const parsed = new URL(trimmedValue, window.location.href);
    parsed.hash = '';
    parsed.search = '';
    parsed.pathname = ensureTrailingSlash(parsed.pathname || '/');
    return parsed.toString();
  } catch {
    return ensureTrailingSlash(trimmedValue);
  }
}

function resolvePreviewBaseUrl(previewUrl?: string): string {
  const trimmedPreviewUrl = previewUrl?.trim();

  return trimmedPreviewUrl
    ? normalizePreviewBaseUrl(trimmedPreviewUrl)
    : EXPERIMENT_PREVIEW_URL;
}

function normalizePath(pathname: string): string {
  const withoutTrailingSlash = pathname.replace(/\/+$/, '');
  return withoutTrailingSlash.length > 0 ? withoutTrailingSlash : '/';
}

function getPreviewBasePath(previewUrl: string): string {
  try {
    const parsed = new URL(previewUrl, window.location.href);
    return normalizePath(parsed.pathname || '/');
  } catch {
    return '/';
  }
}

export const EXPERIMENT_PREVIEW_URL =
  configuredPreviewUrl && configuredPreviewUrl.length > 0
    ? normalizePreviewBaseUrl(configuredPreviewUrl)
    : "";

export function normalizeExperimentPreviewRoute(
  route?: string,
  previewUrl?: string,
): string {
  if (!route || !route.trim()) {
    return '/';
  }

  const previewBaseUrl = resolvePreviewBaseUrl(previewUrl);
  const previewBasePath = getPreviewBasePath(previewBaseUrl);

  try {
    const parsed = /^https?:\/\//i.test(route)
      ? new URL(route)
      : new URL(route, previewBaseUrl);
    const pathname = normalizePath(parsed.pathname || '/');

    if (pathname === previewBasePath) {
      return '/';
    }

    if (previewBasePath !== '/' && pathname.startsWith(`${previewBasePath}/`)) {
      return normalizePath(pathname.slice(previewBasePath.length));
    }

    return pathname;
  } catch {
    const withoutQuery = route.split('?')[0].split('#')[0] || '/';
    const normalized = withoutQuery.startsWith('/') ? withoutQuery : `/${withoutQuery}`;
    const pathname = normalizePath(normalized);

    if (pathname === previewBasePath) {
      return '/';
    }

    if (previewBasePath !== '/' && pathname.startsWith(`${previewBasePath}/`)) {
      return normalizePath(pathname.slice(previewBasePath.length));
    }

    return pathname;
  }
}

export function buildExperimentPreviewUrl(
  route?: string,
  previewUrl?: string,
): string {
  const previewBaseUrl = resolvePreviewBaseUrl(previewUrl);

  if (!route || route === '/') {
    return previewBaseUrl;
  }

  if (/^https?:\/\//i.test(route)) {
    return route;
  }

  const trimmedRoute = route.trim();
  if (!trimmedRoute) {
    return previewBaseUrl;
  }

  const normalizedRoute = normalizeExperimentPreviewRoute(
    trimmedRoute,
    previewBaseUrl,
  );

  if (normalizedRoute === '/') {
    return previewBaseUrl;
  }

  try {
    return new URL(`.${normalizedRoute}`, previewBaseUrl).toString();
  } catch {
    return `${previewBaseUrl}${normalizedRoute.slice(1)}`;
  }
}

export function buildExperimentParticipantPreviewPath(
  experimentId: string,
  iterationId?: string | null,
): string {
  const normalizedExperimentId = experimentId.trim();
  const normalizedIterationId = iterationId?.trim();

  return `/preview/experiment/${encodeURIComponent(normalizedExperimentId)}${
    normalizedIterationId
      ? `/iteration/${encodeURIComponent(normalizedIterationId)}`
      : ""
  }`;
}

export function buildExperimentParticipantPreviewUrl(
  experimentId: string,
  iterationId?: string | null,
  previewUrl?: string,
): string {
  const normalizedIterationId = iterationId?.trim();

  if (!previewUrl?.trim() || !normalizedIterationId) {
    return "";
  }

  return buildExperimentPreviewUrl(
    buildExperimentParticipantPreviewPath(experimentId, normalizedIterationId),
    previewUrl,
  );
}
